import { createContext, useContext, useEffect, useId, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { closedMidSave } from '../../lib/feedback'
import { Frame, LEAVES, makeStack, type Dom, type El, type KeyLike, type Kind, type Seen, type Spot } from './frame'
import { useReducedMotion } from './motion'

// The one frame every pop-up in the console is drawn in.
//
// # TWO SHAPES, CHOSEN BY WHAT IS IN IT
//
// A "modal" is a card in the middle of the window, for a short form or a
// yes-or-no question: six fields or fewer, nothing to scroll. A "drawer" is
// the panel on the right edge, for something that is read or worked through:
// a record, an explanation, a long form. The shape follows the content, never
// who is looking at it.
//
// # FORM OR RECORD
//
// A "form" is a dialog whose fields are sent by its main action. A "record"
// shows a thing, with whatever editors and decisions belong to it. The
// difference decides where the keyboard starts (frame.ts, firstFocus): in a
// form, the first field; in a record, Close, because an editor in a record may
// save as it is left and focus must never be put in one for the reader. A
// destructive main action starts on Cancel in either.
//
// # ONE FOOTER
//
// Cancel and the main action are drawn by the frame, in the footer, in the
// same order everywhere: anything else on the left, then Cancel, then the main
// action last. The body scrolls and the footer does not. A destructive or
// negative action is coloured as one, never as the main look, and stands a gap
// away from Cancel. What has to be said about the act is said in the footer
// beside it: how many things are wrong, why the button is off, what cannot be
// undone.
//
// A form whose state lives deep inside the body puts its actions in the same
// footer with DialogFooter. There is one main action at a time: while the
// dialog has its own, a piece inside does not add a second.
//
// # WAYS OUT
//
// Escape, Close and a press on the scrim close it. While a save is out, Close
// and Escape still do, so nobody is held by a request that never answers; the
// scrim and Cancel do not, and the main action stays locked against a second
// send. The request is not cancelled: lib/save.ts says what becomes of its
// answer. A dialog given a `guard` (a key that is shown once) asks before it
// closes by any of them. Close, Cancel and "Close anyway" are marked as ways
// out (frame.ts, LEAVES), so an editor that saves as it is left can tell
// going to one of them from going on to the next field.
//
// # THE PAGE BEHIND
//
// While a dialog is open the page behind is inert and does not scroll, and a
// dialog under another is inert too. Tab goes round the dialog's own controls
// and whatever is marked data-above-dialogs (the toasts), from any starting
// point. Closing returns focus to whatever opened it.
//
// The frame is drawn in a portal on the body so no panel with overflow hidden
// or a transform can clip it. It slides and fades over 240 ms and stays in the
// document for those 240 ms after it is told to close; with reduced motion it
// simply appears and disappears.
//
// What it does is in frame.ts, as plain code the tests run. This file draws it
// (DialogView, which holds no state, so a test can read what each part is
// wired to) and hands the real document to it (domOf).

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
const FIELD = 'input:not([type="hidden"]), select, textarea, [role="radio"]'
const leaves = { [LEAVES]: '' }

export type DialogAction = {
  label: string
  onClick?: () => void
  // The id of a form in the body. The button then submits that form, so the
  // form's own onSubmit, its required fields and Enter in a field all work as
  // they did when the button sat inside it.
  form?: string
  // Somewhere to go, where the main thing to do is open another page.
  href?: string
  // Why it cannot be pressed now, in words. They are drawn beside it: a button
  // that is off with no reason given leaves the reader to guess.
  off?: string | false | null
  // "plain" is an act that is offered and not urged, as taking back a
  // recorded item is.
  tone?: 'primary' | 'destructive' | 'plain'
  // The persona walk's hooks (data-action, data-action-also).
  actionId?: string
  actionAlso?: string
}

export type DialogFooterProps = {
  primary?: DialogAction
  // False where there is nothing to cancel: a record with a link out. An
  // onClick where Cancel backs out of a step inside the dialog and leaves the
  // dialog open.
  cancel?: false | { onClick: () => void }
  // Other things to do from here, drawn on the left, away from the main action.
  more?: ReactNode
  // What has to stay in view beside the button: what the act cannot undo, who
  // decides next.
  note?: ReactNode
  // How many things the form finds wrong now. Said in the footer because the
  // field that is wrong may have scrolled out of view.
  problems?: number
  // A save is out.
  busy?: boolean
}

type Slot = { foot: HTMLElement | null; taken: boolean; id: string; close: () => void; setBusy: (busy: boolean) => void }
export const FrameContext = createContext<Slot | null>(null)

// Whether this is drawn inside a dialog. A piece that is used both on a page
// and in a dialog (a finding's status cell) asks, and puts its buttons in the
// footer only when there is one.
export function useInDialog(): boolean {
  return useContext(FrameContext) !== null
}

function ActionButton({ action, busy, why }: { action: DialogAction; busy: boolean; why?: string }) {
  const tone = action.tone ?? 'primary'
  const cls = 'kit-dialog-main' + (tone === 'primary' ? ' primary' : tone === 'destructive' ? ' kit-dialog-danger' : '')
  if (action.href) {
    return (
      <a className={'button ' + cls} href={action.href} data-action={action.actionId}>
        {action.label}
      </a>
    )
  }
  const off = action.off || ''
  return (
    <button
      type={action.form ? 'submit' : 'button'}
      form={action.form}
      className={cls}
      disabled={busy || off !== ''}
      title={off || undefined}
      aria-describedby={off && why ? why : undefined}
      onClick={action.onClick}
      data-action={action.actionId}
      data-action-also={action.actionAlso}
    >
      {action.label}
    </button>
  )
}

export function FooterContent({ primary, cancel, more, note, problems = 0, busy = false, close, id }: DialogFooterProps & { close?: () => void; id?: string }) {
  const onCancel = cancel === false ? undefined : (cancel?.onClick ?? close)
  // With nothing to do but read, Close in the head is the way out and a second
  // button saying the same would be noise.
  const showCancel = Boolean(onCancel) && Boolean(primary || cancel)
  const off = !busy && primary && !primary.href && primary.off ? primary.off : ''
  const said = problems > 0 || off !== '' || Boolean(note)
  if (!primary && !more && !said && !showCancel) return null
  const why = id ? id + '-why' : undefined
  return (
    <>
      {said && (
        <div className="kit-dialog-say">
          {problems > 0 && (
            <p className="kit-dialog-problems" role="status">
              {problems} thing{problems === 1 ? '' : 's'} to fix
            </p>
          )}
          {off !== '' && (
            <p className="kit-dialog-off" id={why}>
              {off}
            </p>
          )}
          {note && <div className="kit-dialog-note">{note}</div>}
        </div>
      )}
      {more && <div className="kit-dialog-more">{more}</div>}
      {(primary || showCancel) && (
        <div className="kit-dialog-end">
          {showCancel && (
            <button type="button" className="kit-dialog-cancel" disabled={busy} onClick={onCancel} {...leaves}>
              Cancel
            </button>
          )}
          {primary && <ActionButton action={primary} busy={busy} why={why} />}
        </div>
      )}
    </>
  )
}

// The actions of the dialog this is drawn in, from somewhere inside its body.
// With no dialog around it (the same form on a page, or a test that renders
// the form alone) the actions are drawn where it stands.
export function DialogFooter(props: DialogFooterProps) {
  const frame = useContext(FrameContext)
  const busy = Boolean(props.busy)
  const setBusy = frame?.setBusy
  useEffect(() => {
    if (!setBusy) return
    setBusy(busy)
    return () => setBusy(false)
  }, [busy, setBusy])
  if (!frame) {
    return (
      <div className="kit-dialog-inline">
        <FooterContent {...props} />
      </div>
    )
  }
  if (!frame.foot || frame.taken) return null
  return createPortal(<FooterContent {...props} close={frame.close} id={frame.id + '-in'} />, frame.foot)
}

export type DialogViewProps = DialogFooterProps & {
  shape: 'modal' | 'drawer'
  kind: Kind
  title: ReactNode
  children: ReactNode
  // What the dialog asks before it closes, where it guards something.
  guard?: string
  // Open, as against drawn for the 240 ms it takes to leave.
  open: boolean
  shown: boolean
  // Another dialog is open over this one.
  under: boolean
  asking: boolean
  id: string
  act: Pick<Frame, 'live' | 'scrim' | 'closeButton' | 'cancel'> & { keep: () => void; closeAnyway: () => void }
  slot: Slot
  setFoot: (el: HTMLElement | null) => void
}

// The frame as it is drawn. No state and no effects, so calling it gives the
// whole tree and a test can read what the scrim, Close, Cancel and the main
// action are each wired to.
export function DialogView(p: DialogViewProps) {
  const { shape, kind, title, children, guard, open, shown, under, asking, id, act, slot, setFoot, ...actions } = p
  const busy = Boolean(actions.busy)
  const asked = asking && Boolean(guard)
  return (
    <div className={'kit-dialog-root kit-dialog-' + shape + (shown ? ' kit-on' : '')} inert={under || undefined}>
      {/* A press on the scrim that does not close the dialog (a save is out,
          a question is up) must not take focus off whatever had it. */}
      <div className="kit-scrim" onMouseDown={(e) => e.preventDefault()} onClick={act.scrim} aria-hidden="true" />
      <div className="kit-dialog" role="dialog" aria-modal="true" aria-labelledby={id} aria-busy={busy || undefined} data-kind={kind} tabIndex={-1}>
        <header className="kit-dialog-head">
          <h2 id={id} className="kit-dialog-title" tabIndex={-1}>
            {title}
          </h2>
          <button type="button" className="kit-dialog-close" onClick={act.closeButton} {...leaves}>
            Close
          </button>
        </header>
        <FrameContext.Provider value={slot}>
          <div className="kit-dialog-body">{children}</div>
        </FrameContext.Provider>
        <footer className="kit-dialog-foot">
          {asked && (
            <>
              <div className="kit-dialog-say">
                <p className="kit-dialog-ask" role="alert">
                  {guard}
                </p>
              </div>
              <div className="kit-dialog-end">
                <button type="button" className="kit-dialog-cancel" autoFocus onClick={act.keep}>
                  Keep it open
                </button>
                <button type="button" className="kit-dialog-main kit-dialog-danger" onClick={act.closeAnyway} {...leaves}>
                  Close anyway
                </button>
              </div>
            </>
          )}
          {/* Two holders that draw nothing themselves: the dialog's own actions
              first, then what a form inside adds, so the order the keyboard
              walks them in is the order they are read in whichever was drawn
              first. Put away, not removed, while the question is asked. */}
          <div className="kit-dialog-part" hidden={asked}>
            <FooterContent {...actions} busy={busy} close={act.cancel} id={id} />
          </div>
          <div ref={setFoot} className="kit-dialog-part" hidden={asked} />
        </footer>
        {/* Here only while the dialog is open. Its arriving is the dialog
            opening and its going is the dialog closing (Frame.live). */}
        {open && <span ref={act.live} hidden />}
      </div>
    </div>
  )
}

// --------------------------------------------------------- the real document

const stack = makeStack()

type Part = Pick<Element, 'hasAttribute' | 'setAttribute' | 'removeAttribute'> & { classList: Pick<DOMTokenList, 'contains'> }
type Page = { body: { children: ArrayLike<Part> }; documentElement: { style: { overflow: string } } }

let dimmed: Part[] = []

// Everything on the body that is not a dialog or drawn above them goes inert,
// and the page stops scrolling; then both are put back. What was inert before
// the dialog opened is left alone both ways. `page` is the document: a test
// hands in a stand-in, so nothing here asks whether a part is an HTMLElement
// (everything this console puts on the body is one).
export function pageBehind(behind: boolean, page: Page = document): void {
  if (behind) {
    dimmed = Array.from(page.body.children).filter(
      (el) => !el.classList.contains('kit-dialog-root') && !el.hasAttribute('data-above-dialogs') && !el.hasAttribute('inert'),
    )
    for (const el of dimmed) el.setAttribute('inert', '')
    page.documentElement.style.overflow = 'hidden'
    return
  }
  for (const el of dimmed) el.removeAttribute('inert')
  dimmed = []
  page.documentElement.style.overflow = ''
}

const drawn = (el: Element): boolean => el.getClientRects().length > 0

function domOf(anchor: El): Dom {
  const panel = (anchor as HTMLElement).parentElement as HTMLElement
  const body = panel.querySelector<HTMLElement>(':scope > .kit-dialog-body') as HTMLElement
  const foot = panel.querySelector<HTMLElement>(':scope > .kit-dialog-foot') as HTMLElement
  const all = (root: ParentNode, selector: string) => Array.from(root.querySelectorAll<HTMLElement>(selector))
  return {
    active: () => (document.activeElement && document.activeElement !== document.body ? document.activeElement : null),
    holds: (el) => panel.contains(el as Node),
    attached: (el) => document.contains(el as Node),
    // Just after, not now. The open mark arrives and goes while React is
    // committing, and at the end of a commit React puts focus back on whatever
    // had it at the start: a move made in between is undone.
    focus: (el) => queueMicrotask(() => (el as HTMLElement).focus()),
    seen: (): Seen => {
      const fold = body.getBoundingClientRect()
      const main = foot.querySelector<HTMLElement>('.kit-dialog-main')
      const cancel = foot.querySelector<HTMLButtonElement>('.kit-dialog-cancel')
      return {
        kind: 'record',
        guarded: false,
        asking: false,
        fields: all(body, FIELD).map((el) => {
          const at = el.getBoundingClientRect()
          const radio = el.getAttribute('type') === 'radio' || el.getAttribute('role') === 'radio'
          return {
            shown: drawn(el),
            inView: at.top >= fold.top - 1 && at.bottom <= fold.bottom + 1,
            off: el.matches(':disabled'),
            radio,
            checked: radio && ((el as HTMLInputElement).checked === true || el.getAttribute('aria-checked') === 'true'),
          }
        }),
        main: main && drawn(main) ? { destructive: main.classList.contains('kit-dialog-danger'), off: main.matches(':disabled') } : null,
        cancel: cancel !== null && drawn(cancel) && !cancel.disabled,
      }
    },
    at: (spot: Spot) =>
      spot.spot === 'field'
        ? (all(body, FIELD)[spot.field] ?? null)
        : spot.spot === 'main'
          ? foot.querySelector('.kit-dialog-main')
          : spot.spot === 'cancel'
            ? foot.querySelector('.kit-dialog-cancel')
            : spot.spot === 'close'
              ? panel.querySelector('.kit-dialog-close')
              : spot.spot === 'heading'
                ? panel.querySelector('.kit-dialog-title')
                : null,
    stops: () => [
      ...all(panel, FOCUSABLE).map((el) => ({ el, shown: drawn(el), above: false })),
      ...all(document, '[data-above-dialogs]').flatMap((host) => all(host, FOCUSABLE).map((el) => ({ el, shown: drawn(el), above: true }))),
    ],
    panel: () => panel,
    listen: (type, heard) => {
      const on = (e: Event) => heard(e as unknown as KeyLike)
      document.addEventListener(type, on)
      return () => document.removeEventListener(type, on)
    },
    watch: (heard) => {
      const content = new MutationObserver(heard)
      content.observe(panel, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] })
      const size = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(heard)
      size?.observe(foot)
      window.addEventListener('resize', heard)
      return () => {
        content.disconnect()
        size?.disconnect()
        window.removeEventListener('resize', heard)
      }
    },
    transit: (heard) => {
      // In the capture phase, so this is heard before React's own handlers
      // run a blur and redraw. A move that has not arrived by the next task
      // never will: its target went in that redraw.
      let pending = 0
      const left = (e: FocusEvent) => {
        window.clearTimeout(pending)
        const under = e.relatedTarget !== null
        heard(under, false)
        if (under) pending = window.setTimeout(() => heard(false, true))
      }
      const arrived = () => {
        window.clearTimeout(pending)
        heard(false, false)
      }
      document.addEventListener('focusout', left, true)
      document.addEventListener('focusin', arrived, true)
      return () => {
        window.clearTimeout(pending)
        document.removeEventListener('focusout', left, true)
        document.removeEventListener('focusin', arrived, true)
      }
    },
    page: pageBehind,
    footTop: () => (drawn(foot) ? foot.getBoundingClientRect().top : null),
    viewport: () => window.innerHeight,
    // Read by the toasts' own rule in shell.css.
    clear: (px) => (px > 0 ? document.body.style.setProperty('--kit-dialog-clear', px + 'px') : document.body.style.removeProperty('--kit-dialog-clear')),
    now: () => performance.now(),
  }
}

// A box that takes text: holding a key in one is typing.
function takesText(target: El): boolean {
  const el = target as HTMLElement | null
  if (!el || typeof el.tagName !== 'string') return false
  if (el.tagName === 'TEXTAREA' || el.isContentEditable) return true
  return el.tagName === 'INPUT' && !['button', 'submit', 'reset', 'checkbox', 'radio', 'file'].includes((el as HTMLInputElement).type)
}

export function Dialog({
  open,
  onClose,
  title,
  shape,
  kind,
  guard,
  children,
  ...actions
}: DialogFooterProps & {
  open: boolean
  onClose: () => void
  title: ReactNode
  shape: 'modal' | 'drawer'
  kind: Kind
  guard?: string
  children: ReactNode
}) {
  const reduced = useReducedMotion()
  const [mounted, setMounted] = useState(open)
  const [shown, setShown] = useState(false)
  const [foot, setFoot] = useState<HTMLElement | null>(null)
  const [slotBusy, setSlotBusy] = useState(false)
  const [asking, setAsking] = useState(false)
  const id = useId()

  const busy = Boolean(actions.busy) || slotBusy
  const name = typeof title === 'string' && title !== '' ? title : null
  const now = useRef({ onClose, kind, busy, guarded: Boolean(guard), asking, name })
  now.current = { onClose, kind, busy, guarded: Boolean(guard), asking, name }

  const [frame] = useState(
    () =>
      new Frame(
        stack,
        {
          state: () => now.current,
          close: () => {
            // Closed with a save still out: its answer has nowhere to be shown.
            if (now.current.busy) closedMidSave(now.current.name)
            now.current.onClose()
          },
          ask: setAsking,
          onText: takesText,
        },
        domOf,
      ),
  )
  const under = useSyncExternalStore(
    stack.subscribe,
    () => stack.under(frame.id),
    () => false,
  )

  // Mount on open and start the entrance on the next frame; on close, fade
  // out and then leave the document.
  useEffect(() => {
    if (open) {
      setMounted(true)
      const raf = requestAnimationFrame(() => setShown(true))
      return () => cancelAnimationFrame(raf)
    }
    setShown(false)
    setAsking(false)
    const t = window.setTimeout(() => setMounted(false), reduced ? 0 : 240)
    return () => window.clearTimeout(t)
  }, [open, reduced])

  // Nothing left to guard, nothing left to ask.
  useEffect(() => {
    if (!guard) setAsking(false)
  }, [guard])

  if (!mounted) return null

  const view = (
    <DialogView
      {...actions}
      busy={busy}
      shape={shape}
      kind={kind}
      title={title}
      guard={guard}
      open={open}
      shown={shown}
      under={under}
      asking={asking}
      id={id}
      act={{
        live: frame.live,
        scrim: frame.scrim,
        closeButton: frame.closeButton,
        cancel: frame.cancel,
        keep: () => setAsking(false),
        closeAnyway: () => now.current.onClose(),
      }}
      slot={{ foot, taken: Boolean(actions.primary), id, close: frame.cancel, setBusy: setSlotBusy }}
      setFoot={setFoot}
    >
      {children}
    </DialogView>
  )
  // There is no body to draw into on the server, so the frame is drawn in place.
  return typeof document === 'undefined' ? view : createPortal(view, document.body)
}
